import { Chart as ChartJs } from 'chart.js';
import IconButton from 'components/IconButton';
import React, { useEffect, useMemo, useRef } from 'react';
import { CardHeader, Col, Row } from 'reactstrap';

import BarOrDoughnutChart from '../../../components/BarOrDoughnutChart';
import { ChartColorMode } from '../../../components/BarOrDoughnutChart/helper';
import { ChartType } from '../../../types';

export interface TopFindingChartRow {
  key: string;
  label: string;
  activeCount: number;
  drillDownUrl: string;
}

interface Props {
  id: string;
  title: string;
  rows: TopFindingChartRow[];
  chartType: ChartType;
  onViewDetails: () => void;
}

const TOP_GROUP_LIMIT = 10;
const OTHERS_LABEL = 'All Others';
const withoutTrailingId = (label: string) => label.replace(/\s+\[\d+\]$/, '');

/**
 * Cloud Agent-style top-ten visualization. The caller supplies every filtered
 * group; this component keeps the first ten and combines the remainder into a
 * deliberately non-clickable All Others segment.
 */
const TopFindingsChart = ({
  id,
  title,
  rows,
  chartType,
  onViewDetails,
}: Props) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRows = useMemo(() => {
    const firstTen = rows.slice(0, TOP_GROUP_LIMIT);
    const otherCount = rows
      .slice(TOP_GROUP_LIMIT)
      .reduce((total, row) => total + row.activeCount, 0);

    return otherCount > 0
      ? [
          ...firstTen,
          {
            key: 'all-others',
            label: OTHERS_LABEL,
            activeCount: otherCount,
            drillDownUrl: '',
          },
        ]
      : firstTen;
  }, [rows]);

  const urlByLabel = useMemo(
    () => new Map(chartRows.map((row) => [row.label, row.drillDownUrl])),
    [chartRows],
  );
  const labels = chartRows.map((row) => row.label);
  const counts = chartRows.map((row) => row.activeCount);

  useEffect(() => {
    if (chartType !== ChartType.Doughnut) return;

    const canvas = chartContainerRef.current?.querySelector('canvas');
    const chart = canvas ? ChartJs.getChart(canvas) : undefined;
    const tooltip = chart?.options.plugins?.tooltip;
    const legend = chart?.options.plugins?.legend;
    if (!chart || !tooltip || typeof tooltip !== 'object') return;

    tooltip.callbacks = {
      ...tooltip.callbacks,
      label: (context) =>
        `${withoutTrailingId(context.label || '')}: ${
          context.formattedValue || '0'
        }`,
    };

    if (legend && typeof legend === 'object') {
      const generateLabels = legend.labels?.generateLabels;
      if (generateLabels) {
        legend.labels = {
          ...legend.labels,
          generateLabels: (legendChart) =>
            generateLabels(legendChart).map((item) => ({
              ...item,
              text: withoutTrailingId(item.text),
            })),
        };
      }
    }

    chart.update('none');
  }, [chartRows, chartType]);

  const chartHeader = (
    <CardHeader>
      <Row>
        <Col className="d-flex flex-row align-items-baseline">
          <h2 className="d-inline mb-0">Top 10</h2>
          <h5 className="card-category px-1">{title}</h5>
        </Col>
        <Col>
          <div className="d-inline float-right">
            <IconButton
              id={`${id}_view_details`}
              iconClass="bi bi-table"
              size="sm"
              cssClass="padding-2"
              tooltip="View Details"
              onClick={onViewDetails}
            />
          </div>
        </Col>
      </Row>
    </CardHeader>
  );

  return (
    <div ref={chartContainerRef}>
      <BarOrDoughnutChart
        chartType={chartType}
        title={title}
        labels={labels}
        tooltipLabels={[]}
        datasets={[counts]}
        severityFilters={labels}
        customChartHeader={chartHeader}
        colorMode={ChartColorMode.Scale}
        yAxesLabel="Active Findings"
        getDrillDownUrl={(label) => urlByLabel.get(label) || ''}
      />
    </div>
  );
};

export default TopFindingsChart;
